import type { CoachAchievement, CoachLessonPlan, CoachPurchasableUnit, Game, Transaction } from '$lib/types';
import type { Api_Account, BillingFrequencyEnum } from '$lib/types/api/graphql';
import { uniqueBy } from './array';
import { CANCELATION_POLICIES, STATIC_URL } from '../constants';
import { dayjs, type Dayjs } from '@metafy/dates';
import { isNumber } from './numbers';

const priceFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
});
const priceFormatterDecimal = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export function uuidv4() {
  if (crypto?.randomUUID) {
    return crypto.randomUUID();
  } else {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0,
        v = c == 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}

export function formatTime(time: string, timezone = 'UTC') {
  const datetime = dayjs(time).tz(timezone);
  return formatTimeDatetime(datetime);
}

export function formatTimeDatetime(datetime: Dayjs) {
  return datetime.format('h:mma');
}

export function formatVideoDuration(duration: number) {
  // Examples: "5:02", "47:34", "3:35:29"
  const durationObj = dayjs.duration(duration, 'seconds');
  return durationObj.format(durationObj.hours() >= 1 ? 'H:mm:ss' : 'm:ss');
}

/**
 * @param {number} seconds
 * @returns Formatted date (1h 35m, 45m)
 */
export function formatSecondsToHumanTotalHourMinute(seconds: number) {
  const d = Number(seconds);
  const h = Math.floor(d / 3600);
  // eslint-disable-next-line no-mixed-operators
  const m = Math.floor((d % 3600) / 60);

  const hDisplay = h > 0 ? `${h}h ` : '';
  const mDisplay = m > 0 ? `${m}m` : '';
  return hDisplay + mDisplay;
}

// Returns formatted duration (2d 1h, 8h, 45m, 23s)
export function formatDurationToHumanDayHourMinuteSecond(duration) {
  if (!dayjs.isDuration(duration)) {
    throw new Error('Invalid duration object');
  }

  const days = Math.floor(duration.asDays());
  const hours = Math.floor(duration.asHours());
  const minutes = Math.floor(duration.asMinutes());
  const seconds = Math.floor(duration.asSeconds());

  if (days > 0) {
    if (duration.hours() > 0) {
      return `${days}d ${duration.hours()}h`;
    }
    return `${days}d`;
  }

  if (hours > 0) {
    if (duration.minutes() > 0) {
      return `${hours}h ${duration.minutes()}m`;
    }
    return `${hours}h`;
  }

  if (minutes > 0) {
    return `${minutes}m`;
  }

  return `${seconds}s`;
}

/**
 * @param {string} date - ISO8601 Time String
 * @param {*} timezone
 * @returns {string} Formatted date
 */
export function formatDate(date: string, timezone = 'UTC') {
  let dateTimeFormatted = '';
  const datetime = dayjs(date).tz(timezone);
  const diff = datetime.diff(dayjs().tz(timezone), 'day');
  // Date is either in the distant future, or in the past
  if (diff > 1 || diff < 0) {
    dateTimeFormatted = `${datetime.format('MMM DD')} at ${formatTime(date, timezone)}`;
  } else {
    dateTimeFormatted = datetime.calendar();
  }
  return dateTimeFormatted;
}

export function formatDateWithRange(date: string, duration: number, timezone = 'UTC', includeDate = true) {
  const datetime = dayjs(date).tz(timezone);
  const rangetime = `${formatTimeDatetime(datetime)} - ${formatTimeDatetime(datetime.add(duration, 'minutes'))}`;
  if (includeDate) return `${datetime.format('MMM D')}, ${rangetime}`;
  return rangetime;
}

export function formatPolicyTime(date: string, timezone: string, policy: 'none' | 'flexible' | 'moderate' | 'firm') {
  const datetime = dayjs(date).tz(timezone);
  return datetime.subtract(CANCELATION_POLICIES[policy].value, 'days').format('MMM D, h:mma');
}

export function formatCardExpiration(month: number, year: number) {
  return `${new String(month).padStart(2, '0')}/${new String(year).slice(2)}`;
}

/**
 * @param {number} val - whole number
 * @param {boolean} showDecimalPlaces - whether to show decimal places (default: true)
 * @note If the value is a floating number, it will always show decimal places
 * @returns {string} Number as a currency (i.e. $25,000.00)
 */
export function formatCurrency(val, showDecimalPlaces = true) {
  const isFloatingValue = typeof val === 'number' && val % 1 !== 0;
  const formatter = showDecimalPlaces || isFloatingValue ? priceFormatterDecimal : priceFormatter;
  return formatter.format(val);
}

/**
 *
 * @param {any} price - price to format, will default to 0 if not a number
 * @param {object} options - options to pass to formatPrice
 * @param {boolean} options.allowFree - whether to show "Free" when price is 0 (default: false)
 * @param {boolean} options.showDecimalPlaces - whether to show decimal places (default: true)
 * @returns {string} formatted price or "Free" if price is 0 and allowFree is true
 */
export function formatPrice(price: any, options: { allowFree?: boolean; showDecimalPlaces?: boolean } = {}): string {
  const defaultsOptions = { allowFree: false, showDecimalPlaces: true };
  options = { ...defaultsOptions, ...options };
  price = !isNumber(price) ? 0 : price;
  return price === 0 && options.allowFree ? 'Free' : `${formatCurrency(price, options.showDecimalPlaces)}`;
}

export function humanizeDuration(duration: number) {
  if (duration === 30) {
    return '30 min';
  } else {
    return `${duration / 60} ${duration === 60 ? 'hour' : 'hours'}`;
  }
}

/**
 * @description Humanize a number suffix to a duration in hours, but less than 1 hour is displayed as minutes
 * @param {number} duration number in hours
 * @param {boolean} options.short (i.e. hours)
 * @param {boolean} options.shorter (i.e. hrs)
 */
export function durationSuffix(duration: number, options: { short?: boolean; shorter?: boolean } = {}) {
  // Durations under 1 hour are displayed as minutes.

  if (duration < 1) {
    return options.shorter ? 'm' : options.short ? 'mins' : 'minutes';
  }
  if (duration === 1) {
    return options.shorter || options.short ? 'hr' : 'hour';
  }
  return options.shorter || options.short ? 'hrs' : 'hours';
}

/**
 * @description Humanize a number to a duration in hours, but less than 1 hour is displayed as minutes
 * @param {number} duration number in hours
 * @param {boolean} options.short (i.e. 40 mins)
 * @param {boolean} options.shorter (i.e. 40 m )
 */
export function humanizeDurationHours(
  duration: number,
  options: { short?: boolean; shorter?: boolean; removeSpace?: boolean } = {}
) {
  // Durations under 1 hour are displayed as minutes.
  return `${duration < 1 ? Math.floor(60 * duration) : duration}${options?.removeSpace ? '' : ' '}${durationSuffix(duration, options)}`;
}

/**
 * @description Humanize a number to a duration in hours and minutes
 * @example humanizeDurationMinutes(400) = 6 hours 40 minutes
 * @param {number} duration number in minutes
 * @param {boolean} options.short (i.e. 6 hrs 40 mins)
 * @param {boolean} options.shorter (i.e. 6 hrs 40 m )
 */
export function humanizeDurationMinutes(
  duration: number,
  options: { short?: boolean; shorter?: boolean; removeSpace?: boolean } = {}
) {
  return duration < 60
    ? `${humanizeDurationHours(duration / 60, options)}`
    : `${humanizeDurationHours(Math.floor(duration / 60), options)} ${duration % 60 ? humanizeDurationHours((duration % 60) / 60, options) : ''}`;
}

/**
 * Returns the plural of a word by appending an 's' to the end.
 */
export function pluralize(word: string, count: number, includeCount = true, suffix = 's') {
  return plural(word, count, { includeCount, plural: suffix });
}

/**
 * Returns the plural of a word by appending an 's' to the end.
 * but can also specify a singular suffix / plural suffix for irregular words
 * such as 'match' -> 'matches', or 'reply' -> 'replies'
 * @param word
 * @param count
 * @param includeCount default true
 * @param singular default ''
 * @param plural default 's'
 * @returns string
 */
export function plural(
  word: string,
  count: number,
  {
    includeCount = true,
    singular = '',
    plural = 's'
  }: {
    includeCount?: boolean;
    singular?: string;
    plural?: string;
  } = {}
): string {
  if (word == null || count == null) return '';
  return (includeCount ? count.toString() + ' ' : '') + word + (count !== 1 ? plural : singular);
}

/**
 * Returns the plural for a lesson's units (sessions or matches) with their correct suffixes.
 */
export function pluralizeLessonUnits(lesson: CoachPurchasableUnit, count: number, includeCount = true) {
  const word = lesson.type === 'live' ? 'session' : 'match';
  const suffix = lesson.type === 'live' ? 's' : 'es';
  return pluralize(word, count, includeCount, suffix);
}

export function ordinalSuffix(i: number) {
  const j = i % 10;
  const k = i % 100;
  if (j === 1 && k !== 11) {
    return i + 'st';
  }
  if (j === 2 && k !== 12) {
    return i + 'nd';
  }
  if (j === 3 && k !== 13) {
    return i + 'rd';
  }
  return i + 'th';
}

const ICONS = {
  1: 'gold',
  2: 'silver',
  3: 'bronze'
};
export function getAchievementIconURL(a: CoachAchievement) {
  let icon = 'medal';
  if (a.type === 'tournament') {
    if (a.placement in ICONS) {
      icon = ICONS[a.placement];
    }
  } else if (a.type === 'season') {
    icon = 'season';
  } else {
    icon = 'other';
  }
  return `${STATIC_URL}/coach/achievement_${icon}.svg`;
}

export function displayPlanGames(plan: CoachLessonPlan) {
  const uniqueGames = uniqueBy(
    plan.lessons.filter(l => l.game || l.coachGame).map(m => (m.game ? m.game : m)),
    'id'
  ) as Game[];
  return displayUniqueGames(uniqueGames);
}

export function displayUniqueGames(uniqueGames: Game[]) {
  if (uniqueGames.length === 0) {
    return '0 games';
  }
  return uniqueGames.length > 1 ? `${uniqueGames.length} games` : uniqueGames[0].title.en;
}

/**
 * Returns a human friendly representation of a training plan state.
 */
export function displayPlanState(state: string) {
  switch (state) {
    case 'pending':
      return 'Pending';
    case 'confirmed':
      // Note: We show 'confirmed' as 'In Progress'
      return 'In Progress';
    case 'completed':
      return 'Completed';
    case 'canceled':
      return 'Canceled';
    case 'disputed':
      return 'Disputed';
    default:
      return 'Unknown';
  }
}

/**
 * Returns a human friendly representation of a replay review state.
 */
export function displayReviewState(state: string, scheduledDiff = 0) {
  switch (state) {
    case 'draft':
      return 'Draft';
    case 'purchased':
      return 'Purchased';
    case 'pending':
      return 'Pending';
    case 'in-progress':
      return scheduledDiff > 3
        ? 'In Progress'
        : scheduledDiff === 0
          ? 'Due Today'
          : scheduledDiff === 1
            ? 'Due Tomorrow'
            : scheduledDiff > 1
              ? `Due in ${pluralize('Day', scheduledDiff)}`
              : `${pluralize('Day', Math.abs(scheduledDiff))} Past Due`;
    case 'rejected':
      return 'Rejected';
    case 'expired':
      return 'Expired';
    case 'completed':
      return 'Completed';
    case 'finalized':
      return 'Finalized';
    default:
      return 'Unknown';
  }
}

/**
 * Returns a human friendly representation of a live session's state.
 */
export function displayLiveSessionState(state: string) {
  switch (state) {
    case 'pending':
      return 'Pending';
    case 'rescheduling':
      return 'Rescheduling';
    case 'confirmed':
      // Note: We show 'confirmed' as 'Upcoming'
      return 'Upcoming';
    case 'completed':
      return 'Completed';
    case 'canceled':
      return 'Canceled';
    case 'disputed':
      return 'Disputed';
    default:
      return 'Unknown';
  }
}

export function parseAndCapitalize(str: string) {
  if (!str) return '';
  return str
    .split('_')
    .map(x => `${x.charAt(0).toUpperCase()}${x.slice(1)}`)
    .join(' ');
}

function displayPaymentCheck({ sourceFor }: Transaction) {
  switch (sourceFor) {
    case 'lesson':
      return 'Training Plan Purchase';
    case 'booking':
      return 'Session Purchase';
    case 'tip':
      return 'Tip for Expert';
    case 'gift_card':
      return 'Gift Card Purchase';
    case 'credit':
      return 'Metafy Credit Purchase';
    case 'cancelation_fee':
      return 'Cancelation Credit';
    default:
      return parseAndCapitalize(sourceFor);
  }
}

function displayBalanceCheck({ sourceFor }: Transaction) {
  switch (sourceFor) {
    case 'gift_card':
      return `Gift Card Redemption`;
    case 'live':
      return 'Live Session Completed';
    case 'replay':
      return 'Replay Review Completed';
    case 'lesson':
      return 'Training Plan Accepted';
    case 'tip':
      return 'Tip Received';
    case 'payout':
      return 'Payout Request';
    case 'referral':
      return 'Referral Bonus';
    case 'refund':
      return 'Refund (Support)';
    case 'adjustment':
      return 'Transaction Adjustment (Support)';
    case 'good_will':
      return 'Credited (Support)';
    case 'ambassador_coaching_bonus':
      return 'Ambassaor Bonus';
    case 'ambassador_referral_bonus':
      return 'Ambassaor Referral';
    case 'contest':
      return 'Contest Payout';
    case 'cancelation_fee':
      return 'Cancelation Credit';
    case 'top_mentor':
      return 'A Strange Reward';
    case 'supporter':
      return 'Supporter Share';
    case 'membership_voucher':
      return 'Membership Credit';
    default:
      return parseAndCapitalize(sourceFor);
  }
}

export function displayTransactionSource(transaction: Transaction) {
  const { source, sourceFor } = transaction;
  switch (source) {
    case 'balance':
      return sourceFor ? displayBalanceCheck({ sourceFor } as Transaction) : 'Metafy Credits';
    case 'package':
      return 'Live session bundle';
    case 'lesson':
      if (sourceFor === 'live') return 'Live Training Plan Redemption';
      if (sourceFor === 'replay') return 'Replay Training Plan Redemption';
      return 'Training Plan Booking';
    case 'booking':
      if (sourceFor === 'live') return 'Live Unit Redemption';
      if (sourceFor === 'replay') return 'Replay Unit Redemption';
      return 'Lesson Booking';
    case 'payout':
      return 'Balance Withdrawn';
    case 'gift_card':
      return 'Metafy Gift Card';
    case 'tip':
      return 'Tip';
    case 'payment':
      return sourceFor ? displayPaymentCheck({ sourceFor } as Transaction) : 'Coach Purchase';
    case 'refund':
      return 'Refund';
    case 'cancelation_fee':
      return 'Reschedule Fee';
    default:
      return parseAndCapitalize(source);
  }
}

export function capitalize(str: string) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

export function displayLanguages(langs: string[]) {
  return commaAmpersandList(langs, { separator: ', ', and: ' & ' });
}

export function commaAmpersandList(
  list: string[],
  options: { separator?: string; and?: string } = { separator: ', ', and: ' & ' }
) {
  if (list.length === 1) return list[0];
  return `${list.slice(0, list.length - 1).join(options.separator)}${options.and}${list[list.length - 1]}`;
}

export function getAvailabilityFormat({
  account,
  timezone = dayjs.tz.guess(),
  shortHand = false,
  availableStr = 'Available'
}: {
  account: Api_Account;
  timezone?: string;
  shortHand?: boolean;
  availableStr?: string;
}) {
  if (!account) {
    return ['', ''];
  }
  if (account.isAfk) {
    return [shortHand ? 'Unavailable' : 'Taking a break, back soon!'];
  }

  if (account.nextAvailability === null) {
    const nextMonth = dayjs.utc().tz(timezone).add(1, 'month').format('MMMM');
    return [shortHand ? 'Fully booked' : `Fully booked until ${nextMonth}`];
  }

  const djs = dayjs.utc(account.nextAvailability).tz(timezone);
  const format = djs.calendar(null, {
    sameDay: shortHand ? '[Today]' : 'h:mma [today]',
    nextDay: shortHand ? '[Tomorrow]' : 'h:mma [tomorrow]',
    nextWeek: 'h:mma dddd',
    sameElse: 'll'
  });
  return [availableStr, format];
}

export function getProviderText(provider: string, account?: any) {
  if (!provider || !account) return null;
  switch (provider) {
    case 'discord':
      if (!account) return null;
      // if discriminator equals 0 then its a migrated account and only return the username
      if (account.discriminator === '0') return account.username;
      return `${account.username}#${account.discriminator}`;
    case 'google':
      return account.email;
    case 'twitch':
      return account.username;
    case 'apple':
    case 'facebook':
    case 'patreon':
      return 'Connected';
    default:
      return null;
  }
}

/**
 * return a readable version of a large number, eg;
 * 0 -> '',
 * 277 -> '277'
 * 1000 -> '1k+',
 * 1951 -> '2.0k'
 * 1000022 -> '1.0m'
 * @param count number
 * @param decimals number of decimals to show for numbers between 1000 and 1000000
 * @returns string
 */
export function formatReadableCount(count: number | undefined, decimals = 1): string {
  if (typeof count !== 'number') return '';
  if (count < 1000) return count.toFixed(0);
  if (count < 1000000)
    return !decimals || !(count % 1000) ? `${Math.round(count / 1000)}k+` : `${(count / 1000).toFixed(decimals)}k`;
  else
    return !decimals || !(count % 1000000)
      ? `${Math.round(count / 1000000)}m+`
      : `${(count / 1000000).toFixed(decimals)}m`;
}

/**
 * @description Formats a number to a human readable string with commas and optionally a compact notation (i.e. 1.2k, 1.2m, 1.2b)
 * @param num number to be formatted
 * @param compact whether to use a compact notation (i.e. 1.2k, 1.2m, 1.2b) (default is false)
 * @param locale locale to be used (default is current locale)
 * @returns qstring
 */
export function formatNumber(num: number = 0, compact: boolean = false, locale: string | undefined = undefined) {
  return num.toLocaleString(locale, {
    notation: compact ? 'compact' : 'standard',
    compactDisplay: 'short'
  });
}

/**
 * @description Return a string in a camelCase format
 * @param str string to be formatted
 * @returns string
 */
export function toCamelCase(str: string) {
  return (
    str.slice(0, 1).toLowerCase() +
    str
      .slice(1)
      .replace(/[^a-zA-Z0-9]/g, ' ')
      .replace(/\s(.)/g, $1 => $1.toUpperCase())
      .replace(/\s/g, '')
  );
}

/**
 * @description Truncate a string to a certain length, optionally adding an ellipsis
 * @param str string to be truncated
 * @param length length to truncate to (default is 140)
 * @param addEllipsis whether to add an ellipsis (default is true)
 * @returns string
 */
export function hardTruncate(str: string, length: number = 140, addEllipsis = true) {
  if (!str) return '';
  if (str.length <= length) return str;
  const ellipsis = addEllipsis ? '...' : '';
  return str.slice(0, length - ellipsis.length) + ellipsis;
}

/**
 * Returns a label based on the selected frequency.
 */
export function getLabelBasedOnFrequency(
  frequency: BillingFrequencyEnum,
  billingInterval: number = 1,
  includeCount = false
) {
  switch (frequency) {
    case 'DAY':
      return plural('day', billingInterval, { includeCount: billingInterval > 1 && includeCount });
    case 'WEEK':
      return plural('week', billingInterval, { includeCount: billingInterval > 1 && includeCount });
    case 'MONTH':
      return plural('month', billingInterval, { includeCount: billingInterval > 1 && includeCount });
    case 'YEAR':
      return plural('year', billingInterval, { includeCount: billingInterval > 1 && includeCount });
    default:
      return plural('month', billingInterval, { includeCount: billingInterval > 1 && includeCount });
  }
}
